import { Dispatch, SetStateAction, useCallback } from 'react';
import { SetURLSearchParams, useNavigate } from 'react-router-dom';

import { CapacityFilter } from './capacity';
import { CountryFilter } from './countries';
import { DevelopmentStatusFilter } from './development-status';

import { SearchFilter } from './search';
import { TechnologyFilter } from './technology';
import { Button, Chip } from '@/components';
import { Table } from '@tanstack/react-table';
import { OpportunityItemList } from '@/features/opportunities/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SlidersHorizontal } from 'lucide-react';
import { DealTypeFilter } from './deal-type';
import { TypeRevenueFilter } from './type-of-revenue';

export type OpportunitiesFilterType = {
  readonly onResetFilters: () => void;
  readonly table: Table<OpportunityItemList>;
  readonly datasetMaxCapacity?: number;
  readonly search: string;
  readonly onSearchChange: Dispatch<SetStateAction<string>>;
  readonly searchParams: URLSearchParams;
  readonly onParamsChange: SetURLSearchParams;
};

export function OpportunitiesFilter({
  onResetFilters,
  table,
  datasetMaxCapacity,
  search,
  onSearchChange,
  searchParams,
  onParamsChange,
}: OpportunitiesFilterType) {
  const onChange = useCallback(
    (field: string, rawValue: string | string[]) => {
      let valueArray: string[] = [];

      if (Array.isArray(rawValue)) {
        valueArray = rawValue;
      } else if (typeof rawValue === 'string') {
        valueArray = rawValue.split(',').map((s) => s.trim());
      }

      const value = Array.from(new Set(valueArray.filter(Boolean))).join(',');

      onParamsChange((prev: any) => {
        const searchParams = new URLSearchParams(prev);
        if (value.length === 0) searchParams.delete(field);
        else searchParams.set(field, value);

        // reset pagination:
        searchParams.set('page', '1');
        return searchParams;
      });
    },
    [onParamsChange]
  );

  const resetFilters = useCallback(() => {
    onResetFilters();
    onSearchChange('');
  }, [onResetFilters, onSearchChange]);

  const activeFiltersCount = [
    searchParams.get('deal_type'),
    searchParams.get('development_status'),
    searchParams.get('capacity'),
    searchParams.get('country'),
    searchParams.get('type_of_revenue'),
  ].filter(Boolean).length;

  return (
    <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
      {/* Filters and Search - align to left in desktop*/}
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid w-full grid-cols-2 gap-2 lg:flex lg:w-auto">
          <div className="col-span-1">
            <TechnologyFilter
              table={table}
              value={searchParams.get('technology')?.split(',').filter(Boolean) ?? []}
              onChange={(value) => onChange('technology', value)}
            />
          </div>
          <div className="hidden lg:contents">
            <DevelopmentStatusFilter
              table={table}
              value={searchParams.get('development_status')?.split(',') ?? []}
              onChange={(value) => onChange('development_status', value)}
            />
            <DealTypeFilter
              table={table}
              value={searchParams.get('deal_type')?.split(',') ?? []}
              onChange={(value) => onChange('deal_type', value)}
            />
            <CountryFilter
              table={table}
              value={searchParams.get('country')?.split(',') ?? []}
              onChange={(value) => onChange('country', value)}
            />
            <CapacityFilter
              table={table}
              value={searchParams.get('capacity')?.split(',') ?? []}
              onChange={(value) => onChange('capacity', value)}
              datasetMaxOverride={datasetMaxCapacity}
            />
            <TypeRevenueFilter
              table={table}
              value={searchParams.get('type_of_revenue')?.split(',') ?? []}
              onChange={(value) => onChange('type_of_revenue', value)}
              popoverFilter={true}
            />
          </div>
          <div className="col-span-1 lg:hidden">
            <Popover>
              <PopoverTrigger className="w-full">
                <div className="group relative inline-flex h-9 w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-neutral-50 bg-white px-2 py-2 text-sm font-medium transition-colors outline-none hover:bg-neutral-100 data-[state=open]:bg-neutral-100">
                  <SlidersHorizontal className="h-4 w-4" />
                  More Filters
                  {activeFiltersCount > 0 && (
                    <span className="bg-primary-700 absolute -top-3 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-medium text-white">
                      {activeFiltersCount}
                    </span>
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-auto min-w-64" align="start" side="bottom">
                <div className="flex flex-col gap-4">
                  <DevelopmentStatusFilter
                    table={table}
                    value={searchParams.get('development_status')?.split(',') ?? []}
                    onChange={(value) => onChange('development_status', value)}
                    popoverFilter={true}
                  />
                  <DealTypeFilter
                    table={table}
                    value={searchParams.get('deal_type')?.split(',') ?? []}
                    onChange={(value) => onChange('deal_type', value)}
                  />
                  <CountryFilter
                    table={table}
                    value={searchParams.get('country')?.split(',') ?? []}
                    onChange={(value) => onChange('country', value)}
                    popoverFilter={true}
                  />
                  <CapacityFilter
                    table={table}
                    value={searchParams.get('capacity')?.split(',') ?? []}
                    onChange={(value) => onChange('capacity', value)}
                    datasetMaxOverride={datasetMaxCapacity}
                    popoverFilter={true}
                  />
                  <TypeRevenueFilter
                    table={table}
                    value={searchParams.get('type_of_revenue')?.split(',') ?? []}
                    onChange={(value) => onChange('type_of_revenue', value)}
                    popoverFilter={true}
                  />
                  <Button color="neutral" onClick={resetFilters} className="ml-auto">
                    Reset Filters
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Search - next to filters in desktop */}
        <div className="w-full lg:w-auto">
          <SearchFilter defaultValue={search} onChange={onSearchChange} />
        </div>
      </div>

      {/* Reset Button - align to right in desktop */}
      <div className="hidden lg:block">
        <Button color="neutral" onClick={resetFilters} className="whitespace-nowrap">
          Reset Filters
        </Button>
      </div>
    </div>
  );
}
